import { withHTMLComponent } from '@updater-legacy/components/custom-element';
import styles from './index.css';
import html from './index.html';

const SPACE_KEY = 32;
const OBSERVED_ATTRS = ['disabled', 'helper', 'label', 'value'];

class RadioOption extends withHTMLComponent(styles, html)() {
  static observedAttributes = OBSERVED_ATTRS;
  static reflectedAttributes = OBSERVED_ATTRS.concat([
    'aria-checked',
    'aria-disabled',
    'aria-label',
    'role',
    'tabindex',
  ]);
  static attributeTypes = {
    disabled: 'boolean',
  };

  constructor() {
    super();
    this._label = this.shadowRoot.querySelector('#label');
    this._labelSlot = this.shadowRoot.querySelector('slot[name="label"]');
    this._helper = this.shadowRoot.querySelector('#helper');
    this._helperSlot = this.shadowRoot.querySelector('slot[name="helper"]');
  }

  connected() {
    this.role = this.role || 'radio';

    this.addEventListener('click', this._handleClick);
    this.addEventListener('keyup', this._handleKeyPress);
    this.addEventListener('keydown', this._handleKeyPress);
    this._labelSlot.addEventListener('slotchange', () => {
      if (this._labelSlot.assignedNodes()) {
        [...this._labelSlot.assignedNodes()].forEach((el) => {
          el.addEventListener('click', this._handleSlotClick);
        });

        this.ariaLabel = this._labelSlot
          .assignedNodes()
          .map((n) => n.textContent)
          .join();
      }
    });
    this._helperSlot.addEventListener('slotchange', () => {
      if (this._helperSlot.assignedNodes()) {
        [...this._helperSlot.assignedNodes()].forEach((el) => {
          el.addEventListener('click', this._handleSlotClick);
        });
      }
    });

    // In polyfilled browsers, `radio-option`s are added to the DOM before the
    // `radio-group` parent. Wait to ensure the parent is present before adding
    // the listener. TODO: Remove, if and when we can drop the polyfill
    Promise.resolve().then(() => {
      this.dispatchEvent(
        new CustomEvent('updradiooptionconnected', {
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  disconnected() {
    this.dispatchEvent(
      new CustomEvent('updradiooptiondisconnected', {
        bubbles: true,
        composed: true,
      })
    );

    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keyup', this._handleKeyPress);
    this.removeEventListener('keydown', this._handleKeyPress);
    if (this._labelSlot.assignedNodes()) {
      [...this._labelSlot.assignedNodes()].forEach((el) => {
        el.removeEventListener('click', this._handleSlotClick);
      });
    }
    if (this._helperSlot.assignedNodes()) {
      [...this._helperSlot.assignedNodes()].forEach((el) => {
        el.removeEventListener('click', this._handleSlotClick);
      });
    }
  }

  get checked() {
    return this.ariaChecked === 'true';
  }

  updated({ disabled, helper, label }) {
    this._handleDisabled(disabled);
    if (helper) {
      // only set textContent if helper is supplied as an attribute
      // thereby preserving the slot in html if helper is html and not string
      this._helper.textContent = helper;
    }
    if (label) {
      this._label.textContent = label;
      this.ariaLabel = label;
    }
  }

  _handleClick = (event) => {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  _handleSlotClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    // reroute slot-click through shadow-dom
    // so target is upd-radio-option
    this.dispatchEvent(
      new Event('click', {
        bubbles: true,
        composed: true,
      })
    );
  };

  _handleDisabled(isDisabled) {
    this.ariaDisabled = isDisabled.toString();
    this.tabIndex = isDisabled ? null : -1;
    this.blur();
  }

  _handleKeyPress = (event) => {
    // Don’t handle modifier shortcuts typically used by assistive technology.
    if (event.altKey || this.disabled) {
      return;
    }

    if (event.keyCode === SPACE_KEY) {
      // Prevent scrolling on space key press
      event.preventDefault();

      if (event.type === 'keyup') {
        this.dispatchEvent(
          new Event('click', {
            bubbles: true,
            composed: true,
          })
        );
      }
    }
  };
}

window.customElements.define('upd-radio-option', RadioOption);
